import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  IconButton,
  Stack,
  Typography,
  Chip,
  AspectRatio,
  Card,
  Tooltip,
  Menu,
  MenuItem,
  ListItemDecorator,
  Select,
  Option,
  Slider,
  AccordionDetails,
  AccordionSummary,
  Dropdown,
  MenuButton,
} from '@mui/joy';
import { TabbableAccordion } from '../../../components/common/TabbableAccordion';
import {
  Sparkle as AIIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
  Image as ImageIcon,
  X as CloseIcon,
  ArrowsClockwise as RefreshIcon,
  Brain as ToneIcon,
  CaretDown as ExpandMoreIcon,
  Gear as SettingsIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { MediaPicker } from '../../../components/cms/MediaPicker';
import { AIContentPanel } from '../../../components/cms/AIContentPanel';
import { ColorPicker } from '../../../components/cms/ColorPicker';

interface SectionEditorProps {
  section: { type: string; data: Record<string, any> };
  sectionConfig: any;
  onSave: (data: Record<string, any>) => void;
  onClose: () => void;
  aiSettings?: any;
  contentBrief?: string;
  siteId?: number;
  brandColors?: {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    quaternary?: string;
    quinary?: string;
  };
}

const TONE_PRESETS = [
  { value: 'professional', label: 'Professional', icon: '💼' },
  { value: 'friendly', label: 'Friendly', icon: '😊' },
  { value: 'urgent', label: 'Urgent', icon: '⚡' },
  { value: 'educational', label: 'Educational', icon: '📚' },
];

const PRESET_COLORS = [
  { label: 'White', value: '#FFFFFF' },
  { label: 'Black', value: '#000000' },
  { label: 'Dark Gray', value: '#374151' },
  { label: 'Gray', value: '#6B7280' },
  { label: 'Light Gray', value: '#9CA3AF' },
];

// Inline Color Picker component for text fields
interface InlineColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  brandColors?: {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    quaternary?: string;
    quinary?: string;
  };
}

const InlineColorPicker: React.FC<InlineColorPickerProps> = ({ value, onChange, brandColors }) => {
  const [customColor, setCustomColor] = useState(value || '#000000');

  const handleColorSelect = (color: string) => {
    onChange(color);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange(newColor);
  };

  const clearColor = () => {
    onChange('');
  };

  const hasBrandColors = brandColors?.primary || brandColors?.secondary || brandColors?.tertiary;

  return (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm', title: 'Text color' } }}
      >
        {value ? (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: 'sm',
              backgroundColor: value,
              border: '2px solid',
              borderColor: 'divider',
            }}
          />
        ) : (
          <PaletteIcon />
        )}
      </MenuButton>
      <Menu placement="bottom-end" sx={{ minWidth: 200, p: 1.5, zIndex: 9999 }}>
        {/* Brand Colors */}
        {hasBrandColors && (
          <>
            <Typography level="body-xs" sx={{ mb: 1, fontWeight: 'bold' }}>Brand Colors</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
              {brandColors?.primary && (
                <Tooltip title="Primary">
                  <Box
                    onClick={() => handleColorSelect(brandColors.primary!)}
                    sx={{
                      width: 28,
                      height: 28,
                      backgroundColor: brandColors.primary,
                      border: '2px solid',
                      borderColor: value === brandColors.primary ? 'primary.500' : 'divider',
                      borderRadius: 'sm',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { borderColor: 'primary.300' },
                    }}
                  >
                    {value === brandColors.primary && <CheckIcon color="white" size={14} />}
                  </Box>
                </Tooltip>
              )}
              {brandColors?.secondary && (
                <Tooltip title="Secondary">
                  <Box
                    onClick={() => handleColorSelect(brandColors.secondary!)}
                    sx={{
                      width: 28,
                      height: 28,
                      backgroundColor: brandColors.secondary,
                      border: '2px solid',
                      borderColor: value === brandColors.secondary ? 'primary.500' : 'divider',
                      borderRadius: 'sm',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { borderColor: 'primary.300' },
                    }}
                  >
                    {value === brandColors.secondary && <CheckIcon color="white" size={14} />}
                  </Box>
                </Tooltip>
              )}
              {brandColors?.tertiary && (
                <Tooltip title="Accent">
                  <Box
                    onClick={() => handleColorSelect(brandColors.tertiary!)}
                    sx={{
                      width: 28,
                      height: 28,
                      backgroundColor: brandColors.tertiary,
                      border: '2px solid',
                      borderColor: value === brandColors.tertiary ? 'primary.500' : 'divider',
                      borderRadius: 'sm',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { borderColor: 'primary.300' },
                    }}
                  >
                    {value === brandColors.tertiary && <CheckIcon color="white" size={14} />}
                  </Box>
                </Tooltip>
              )}
            </Box>
          </>
        )}

        {/* Preset Colors */}
        <Typography level="body-xs" sx={{ mb: 1, fontWeight: 'bold' }}>Preset Colors</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
          {PRESET_COLORS.map((color) => (
            <Tooltip key={color.value} title={color.label}>
              <Box
                onClick={() => handleColorSelect(color.value)}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: color.value,
                  border: '2px solid',
                  borderColor: value === color.value ? 'primary.500' : 'divider',
                  borderRadius: 'sm',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { borderColor: 'primary.300' },
                }}
              >
                {value === color.value && (
                  <CheckIcon color={color.value === '#FFFFFF' ? 'var(--joy-palette-text-primary)' : 'white'} size={14} />
                )}
              </Box>
            </Tooltip>
          ))}
        </Box>

        {/* Custom Color Picker */}
        <Typography level="body-xs" sx={{ mb: 1, fontWeight: 'bold' }}>Custom Color</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <input
            type="color"
            value={customColor}
            onChange={handleCustomColorChange}
            style={{
              width: 36,
              height: 28,
              border: '1px solid #E5E7EB',
              borderRadius: 4,
              cursor: 'pointer',
              padding: 0,
            }}
          />
          <Input
            size="sm"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Clear Button */}
        {value && (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={clearColor}
            sx={{ mt: 1, width: '100%' }}
          >
            Clear Color
          </Button>
        )}
      </Menu>
    </Dropdown>
  );
};

// Remote Select Field Component - Fetches options from API endpoint
interface RemoteSelectFieldProps {
  field: {
    name: string;
    label: string;
    endpoint: string;
    value_field: string;
    label_field: string;
    required?: boolean;
    help_text?: string;
  };
  value: any;
  onChange: (value: any) => void;
}

const RemoteSelectField: React.FC<RemoteSelectFieldProps> = ({ field, value, onChange }) => {
  const { data: options = [], isLoading, error } = useQuery<Record<string, any>[]>({
    queryKey: ['remote-select', field.endpoint],
    queryFn: async (): Promise<Record<string, any>[]> => {
      const response = await api.get<Record<string, any>[]>(field.endpoint);
      return response.data;
    },
  });

  return (
    <FormControl required={field.required}>
      <FormLabel>{field.label}</FormLabel>
      {field.help_text && (
        <Typography level="body-xs" sx={{ mb: 1 }}>
          {field.help_text}
        </Typography>
      )}
      <Select
        value={value || ''}
        onChange={(_, newValue) => onChange(newValue)}
        placeholder={isLoading ? 'Loading...' : 'Choose an option'}
        disabled={isLoading}
      >
        {error ? (
          <Option value="" disabled>
            Error loading options
          </Option>
        ) : options.length === 0 && !isLoading ? (
          <Option value="" disabled>
            No active forms available
          </Option>
        ) : (
          options.map((option: any) => (
            <Option key={option[field.value_field]} value={option[field.value_field]}>
              {option[field.label_field]}
            </Option>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  sectionConfig,
  onSave,
  onClose,
  aiSettings,
  contentBrief,
  siteId,
  brandColors,
}) => {
  const [formData, setFormData] = useState(section.data);
  const [generating, setGenerating] = useState<string | null>(null);
  const [mediaPickerField, setMediaPickerField] = useState<string | null>(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [toneMenuAnchor, setToneMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTone, setSelectedTone] = useState(aiSettings?.tone || 'professional');
  const [currentGeneratingField, setCurrentGeneratingField] = useState<string | null>(null);

  // AI field generation with tone support
  const generateFieldMutation = useMutation({
    mutationFn: async ({ fieldName, tone, regenerate = false }: { fieldName: string; tone?: string; regenerate?: boolean }) => {
      return api.post('/api/cms/ai/generate-field/', {
        section_type: section.type,
        field_name: fieldName,
        current_data: formData,
        ai_settings: { ...aiSettings, tone: tone || selectedTone },
        content_brief: contentBrief,
        regenerate,
      });
    },
    onSuccess: (response: any, { fieldName }: { fieldName: string }) => {
      const data = response?.data || response;
      if (data?.value) {
        setFormData((prev: any) => ({ ...prev, [fieldName]: data.value }));
      }
    },
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  const handleGenerateField = async (fieldName: string, tone?: string) => {
    setGenerating(fieldName);
    setCurrentGeneratingField(fieldName);
    try {
      await generateFieldMutation.mutateAsync({ fieldName } as any);
    } finally {
      setGenerating(null);
      setCurrentGeneratingField(null);
    }
  };

  const handleRegenerateField = async (fieldName: string, tone?: string) => {
    setGenerating(fieldName);
    try {
      await generateFieldMutation.mutateAsync({ fieldName } as any);
    } finally {
      setGenerating(null);
    }
  };

  const handleOpenToneMenu = (event: React.MouseEvent<HTMLElement>, fieldName: string) => {
    event.stopPropagation();
    setToneMenuAnchor(event.currentTarget);
    setCurrentGeneratingField(fieldName);
  };

  const handleSelectTone = async (tone: string) => {
    setSelectedTone(tone);
    if (currentGeneratingField) {
      await handleRegenerateField(currentGeneratingField, tone);
    }
    setToneMenuAnchor(null);
    setCurrentGeneratingField(null);
  };

  const handleBulkGenerate = (generatedContent: Record<string, any>) => {
    setFormData({ ...formData, ...generatedContent });
    setShowAIPanel(false);
  };

  const handleArrayItemAdd = (fieldName: string) => {
    const currentArray = formData[fieldName] || [];
    setFormData({
      ...formData,
      [fieldName]: [...currentArray, ''],
    });
  };

  const handleArrayItemChange = (fieldName: string, index: number, value: string) => {
    const currentArray = [...(formData[fieldName] || [])];
    currentArray[index] = value;
    setFormData({
      ...formData,
      [fieldName]: currentArray,
    });
  };

  const handleArrayItemDelete = (fieldName: string, index: number) => {
    const currentArray = [...(formData[fieldName] || [])];
    currentArray.splice(index, 1);
    setFormData({
      ...formData,
      [fieldName]: currentArray,
    });
  };

  const handleSave = () => {
    onSave(formData);
  };

  const renderField = (field: any) => {
    const value = formData[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>
              {field.label}
              {field.max_length && (
                <Chip size="sm" sx={{ ml: 1 }}>
                  {value.length}/{field.max_length}
                </Chip>
              )}
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Input
                value={value}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                sx={{ flex: 1 }}
              />
              {field.ai_enabled && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Generate with AI">
                    <IconButton
                      onClick={() => handleGenerateField(field.name)}
                      loading={generating === field.name}
                      color="primary"
                      variant={value ? 'outlined' : 'solid'}
                    >
                      {value ? <RefreshIcon /> : <AIIcon />}
                    </IconButton>
                  </Tooltip>
                  {value && (
                    <Tooltip title="Change tone">
                      <IconButton
                        size="sm"
                        onClick={(e) => handleOpenToneMenu(e, field.name)}
                        color="neutral"
                      >
                        <ToneIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
              <InlineColorPicker
                value={formData[`${field.name}_color`] || ''}
                onChange={(color) => handleFieldChange(`${field.name}_color`, color)}
                brandColors={brandColors}
              />
            </Box>
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>
              {field.label}
              {field.max_length && (
                <Chip size="sm" sx={{ ml: 1 }}>
                  {value.length}/{field.max_length}
                </Chip>
              )}
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Textarea
                value={value}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                minRows={3}
                sx={{ flex: 1 }}
              />
              {field.ai_enabled && (
                <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                  <Tooltip title="Generate with AI">
                    <IconButton
                      onClick={() => handleGenerateField(field.name)}
                      loading={generating === field.name}
                      color="primary"
                      variant={value ? 'outlined' : 'solid'}
                    >
                      {value ? <RefreshIcon /> : <AIIcon />}
                    </IconButton>
                  </Tooltip>
                  {value && (
                    <Tooltip title="Change tone">
                      <IconButton
                        size="sm"
                        onClick={(e) => handleOpenToneMenu(e, field.name)}
                        color="neutral"
                      >
                        <ToneIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <InlineColorPicker
                  value={formData[`${field.name}_color`] || ''}
                  onChange={(color) => handleFieldChange(`${field.name}_color`, color)}
                  brandColors={brandColors}
                />
              </Box>
            </Box>
          </FormControl>
        );

      case 'url':
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>{field.label}</FormLabel>
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder="https://..."
              type="url"
            />
          </FormControl>
        );

      case 'array':
        const arrayValues = formData[field.name] || [];
        return (
          <FormControl key={field.name}>
            <FormLabel>
              {field.label}
              <Button
                size="sm"
                startDecorator={<AddIcon />}
                sx={{ ml: 2 }}
                onClick={() => handleArrayItemAdd(field.name)}
              >
                Add Item
              </Button>
            </FormLabel>
            <Stack spacing={1}>
              {arrayValues.map((item: string, index: number) => (
                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                  <Input
                    value={item}
                    onChange={(e) => handleArrayItemChange(field.name, index, e.target.value)}
                    placeholder={`Item ${index + 1}`}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="sm"
                    color="danger"
                    onClick={() => handleArrayItemDelete(field.name, index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              {arrayValues.length === 0 && (
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  No items added yet
                </Typography>
              )}
            </Stack>
          </FormControl>
        );

      case 'image':
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>{field.label}</FormLabel>
            
            {value && (
              <Card variant="outlined" sx={{ mb: 2, position: 'relative' }}>
                <AspectRatio ratio="16/9">
                  <img
                    src={value}
                    alt={field.label}
                    style={{ objectFit: 'cover' }}
                  />
                </AspectRatio>
                <IconButton
                  size="sm"
                  variant="solid"
                  color="danger"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                  }}
                  onClick={() => handleFieldChange(field.name, '')}
                >
                  <CloseIcon />
                </IconButton>
              </Card>
            )}
            
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder="Image URL or select from library"
              endDecorator={
                <IconButton
                  onClick={() => setMediaPickerField(field.name)}
                  disabled={!siteId}
                >
                  <ImageIcon />
                </IconButton>
              }
            />
            {!siteId && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                Save the page first to access the media library
              </Typography>
            )}
          </FormControl>
        );

      case 'media':
        // Media type is the same as image but with media library support
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>{field.label}</FormLabel>
            {field.help_text && (
              <Typography level="body-xs" sx={{ mb: 1 }}>
                {field.help_text}
              </Typography>
            )}
            
            {value && (
              <Card variant="outlined" sx={{ mb: 2, position: 'relative' }}>
                <AspectRatio ratio="16/9">
                  <img
                    src={value}
                    alt={field.label}
                    style={{ objectFit: 'cover' }}
                  />
                </AspectRatio>
                <IconButton
                  size="sm"
                  variant="solid"
                  color="danger"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                  }}
                  onClick={() => handleFieldChange(field.name, '')}
                >
                  <CloseIcon />
                </IconButton>
              </Card>
            )}
            
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder="Select from media library"
              endDecorator={
                <IconButton
                  onClick={() => setMediaPickerField(field.name)}
                  disabled={!siteId}
                >
                  <ImageIcon />
                </IconButton>
              }
            />
            {!siteId && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                Save the page first to access the media library
              </Typography>
            )}
          </FormControl>
        );

      case 'color':
        return (
          <ColorPicker
            key={field.name}
            value={value}
            onChange={(color) => handleFieldChange(field.name, color)}
            label={field.label}
            helpText={field.help_text}
            brandColors={brandColors || {
              primary: '#2563EB', // Fallback colors if no brand colors are provided
              secondary: '#7C3AED',
              tertiary: '#EC4899',
            }}
          />
        );

      case 'range':
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>
              {field.label}
              {field.suffix && (
                <Chip size="sm" sx={{ ml: 1 }}>
                  {value}{field.suffix}
                </Chip>
              )}
            </FormLabel>
            {field.help_text && (
              <Typography level="body-xs" sx={{ mb: 1 }}>
                {field.help_text}
              </Typography>
            )}
            <Slider
              value={value || field.default || field.min || 0}
              onChange={(_, newValue) => handleFieldChange(field.name, newValue)}
              min={field.min || 0}
              max={field.max || 100}
              step={field.step || 1}
              valueLabelDisplay="auto"
              marks={[
                { value: field.min || 0, label: `${field.min || 0}${field.suffix || ''}` },
                { value: field.max || 100, label: `${field.max || 100}${field.suffix || ''}` },
              ]}
            />
          </FormControl>
        );

      case 'select':
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>{field.label}</FormLabel>
            {field.help_text && (
              <Typography level="body-xs" sx={{ mb: 1 }}>
                {field.help_text}
              </Typography>
            )}
            <Select
              value={value || field.default || ''}
              onChange={(_, newValue) => handleFieldChange(field.name, newValue)}
              placeholder="Choose an option"
            >
              {field.options?.map((option: any) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </FormControl>
        );

      case 'remote_select':
        return (
          <RemoteSelectField
            key={field.name}
            field={field}
            value={value}
            onChange={(newValue) => handleFieldChange(field.name, newValue)}
          />
        );

      case 'boolean':
        return (
          <FormControl key={field.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <input
                type="checkbox"
                id={`field-${field.name}`}
                checked={Boolean(value ?? field.default)}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <FormLabel htmlFor={`field-${field.name}`} sx={{ mb: 0, cursor: 'pointer' }}>
                {field.label}
              </FormLabel>
            </Box>
            {field.help_text && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                {field.help_text}
              </Typography>
            )}
          </FormControl>
        );

      case 'link':
        const linkValue = formData[field.name] || {};
        return (
          <FormControl key={field.name} required={field.required}>
            <FormLabel>{field.label}</FormLabel>
            <Stack spacing={1}>
              {field.fields?.label && (
                <Input
                  value={linkValue.label || ''}
                  onChange={(e) => handleFieldChange(field.name, { ...linkValue, label: e.target.value })}
                  placeholder={field.fields.label.placeholder || 'Button text'}
                  startDecorator={
                    <Typography level="body-xs" sx={{ minWidth: 50 }}>
                      Text:
                    </Typography>
                  }
                />
              )}
              {field.fields?.href && (
                <Input
                  value={linkValue.href || ''}
                  onChange={(e) => handleFieldChange(field.name, { ...linkValue, href: e.target.value })}
                  placeholder={field.fields.href.placeholder || 'URL'}
                  type="url"
                  startDecorator={
                    <Typography level="body-xs" sx={{ minWidth: 50 }}>
                      URL:
                    </Typography>
                  }
                />
              )}
            </Stack>
          </FormControl>
        );

      case 'list':
        const listValues = formData[field.name] || [];
        return (
          <FormControl key={field.name}>
            <FormLabel>
              {field.label}
              <Button
                size="sm"
                startDecorator={<AddIcon />}
                sx={{ ml: 2 }}
                onClick={() => {
                  const newItem: Record<string, any> = {};
                  // Initialize with default values
                  if (field.of) {
                    Object.entries(field.of).forEach(([key, subField]: [string, any]) => {
                      if (subField.default !== undefined) {
                        newItem[key] = subField.default;
                      }
                    });
                  }
                  handleFieldChange(field.name, [...listValues, newItem]);
                }}
                disabled={field.max && listValues.length >= field.max}
              >
                Add {field.label?.replace(/s$/, '') || 'Item'}
              </Button>
            </FormLabel>
            <Stack spacing={2}>
              {listValues.map((item: any, index: number) => (
                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    {field.of && Object.entries(field.of).map(([subFieldName, subField]: [string, any]) => {
                      const subFieldValue = item[subFieldName] || '';
                      
                      // Render sub-fields based on their type
                      switch (subField.type) {
                        case 'text':
                          return (
                            <Input
                              key={subFieldName}
                              value={subFieldValue}
                              onChange={(e) => {
                                const newItems = [...listValues];
                                newItems[index] = { ...newItems[index], [subFieldName]: e.target.value };
                                handleFieldChange(field.name, newItems);
                              }}
                              placeholder={subField.placeholder || subField.label}
                              size="sm"
                              startDecorator={
                                <Typography level="body-xs" sx={{ minWidth: 80 }}>
                                  {subField.label}:
                                </Typography>
                              }
                            />
                          );

                        case 'number':
                          return (
                            <Input
                              key={subFieldName}
                              type="number"
                              value={subFieldValue}
                              onChange={(e) => {
                                const newItems = [...listValues];
                                newItems[index] = { ...newItems[index], [subFieldName]: e.target.value ? Number(e.target.value) : '' };
                                handleFieldChange(field.name, newItems);
                              }}
                              placeholder={subField.placeholder || subField.label}
                              size="sm"
                              startDecorator={
                                <Typography level="body-xs" sx={{ minWidth: 80 }}>
                                  {subField.label}:
                                </Typography>
                              }
                            />
                          );

                        case 'textarea':
                          return (
                            <FormControl key={subFieldName} size="sm">
                              <FormLabel>{subField.label}</FormLabel>
                              <Textarea
                                value={subFieldValue}
                                onChange={(e) => {
                                  const newItems = [...listValues];
                                  newItems[index] = { ...newItems[index], [subFieldName]: e.target.value };
                                  handleFieldChange(field.name, newItems);
                                }}
                                placeholder={subField.placeholder}
                                minRows={subField.rows || 2}
                              />
                            </FormControl>
                          );
                        
                        case 'select':
                          return (
                            <FormControl key={subFieldName} size="sm">
                              <FormLabel>{subField.label}</FormLabel>
                              <Select
                                value={subFieldValue || subField.default || ''}
                                onChange={(_, newValue) => {
                                  const newItems = [...listValues];
                                  newItems[index] = { ...newItems[index], [subFieldName]: newValue };
                                  handleFieldChange(field.name, newItems);
                                }}
                              >
                                {subField.options?.map((opt: any) => (
                                  <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </Option>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        
                        case 'url':
                          return (
                            <Input
                              key={subFieldName}
                              value={subFieldValue}
                              onChange={(e) => {
                                const newItems = [...listValues];
                                newItems[index] = { ...newItems[index], [subFieldName]: e.target.value };
                                handleFieldChange(field.name, newItems);
                              }}
                              placeholder={subField.placeholder || 'https://...'}
                              type="url"
                              size="sm"
                              startDecorator={
                                <Typography level="body-xs" sx={{ minWidth: 80 }}>
                                  {subField.label}:
                                </Typography>
                              }
                            />
                          );

                        case 'media':
                        case 'image':
                          return (
                            <FormControl key={subFieldName}>
                              <FormLabel>{subField.label}</FormLabel>
                              {subFieldValue && (
                                <Card variant="outlined" sx={{ mb: 1, position: 'relative' }}>
                                  <AspectRatio ratio="16/9" sx={{ maxWidth: 200 }}>
                                    <img
                                      src={subFieldValue}
                                      alt={subField.label}
                                      style={{ objectFit: 'contain' }}
                                    />
                                  </AspectRatio>
                                  <IconButton
                                    size="sm"
                                    variant="solid"
                                    color="danger"
                                    sx={{ position: 'absolute', top: 4, right: 4 }}
                                    onClick={() => {
                                      const newItems = [...listValues];
                                      newItems[index] = { ...newItems[index], [subFieldName]: '' };
                                      handleFieldChange(field.name, newItems);
                                    }}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </Card>
                              )}
                              <Input
                                value={subFieldValue}
                                onChange={(e) => {
                                  const newItems = [...listValues];
                                  newItems[index] = { ...newItems[index], [subFieldName]: e.target.value };
                                  handleFieldChange(field.name, newItems);
                                }}
                                placeholder="Select from media library"
                                size="sm"
                                endDecorator={
                                  <IconButton
                                    size="sm"
                                    onClick={() => setMediaPickerField(`${field.name}.${index}.${subFieldName}`)}
                                    disabled={!siteId}
                                  >
                                    <ImageIcon />
                                  </IconButton>
                                }
                              />
                            </FormControl>
                          );

                        default:
                          return null;
                      }
                    })}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        size="sm"
                        color="danger"
                        onClick={() => {
                          const newItems = [...listValues];
                          newItems.splice(index, 1);
                          handleFieldChange(field.name, newItems);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </FormControl>
        );

      case 'group':
      case 'object':
        const groupValue = formData[field.name] || {};
        
        return (
          <TabbableAccordion 
            key={field.name}
            defaultExpanded={!field.collapsed}
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'md',
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              indicator={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'background.level1',
                px: 2,
                py: 1.5,
                minHeight: 48,
                '& .MuiAccordionSummary-button': {
                  gap: 1,
                },
              }}
            >
              <SettingsIcon color="var(--joy-palette-text-secondary)" />
              <Typography level="title-sm">{field.label}</Typography>
              {field.optional && (
                <Chip size="sm" variant="soft" sx={{ ml: 'auto', mr: 1 }}>
                  Optional
                </Chip>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 2 }}>
              <Stack spacing={2}>
                {field.fields && Object.entries(field.fields).map(([subFieldName, subField]: [string, any]) => {
                  // Check show_if condition
                  if (subField.show_if) {
                    const conditions = Object.entries(subField.show_if);
                    const shouldShow = conditions.every(([condField, condValue]: [string, any]) => {
                      if (condValue.$exists !== undefined) {
                        return condValue.$exists ? !!groupValue[condField] : !groupValue[condField];
                      }
                      return groupValue[condField] === condValue;
                    });
                    if (!shouldShow) return null;
                  }
                  
                  // Create a wrapper component to handle the nested field
                  const NestedField = () => {
                    const nestedValue = groupValue[subFieldName] || '';
                    const handleNestedChange = (newValue: any) => {
                      handleFieldChange(field.name, { ...groupValue, [subFieldName]: newValue });
                    };
                    
                    // Render the field based on its type
                    switch (subField.type) {
                      case 'color':
                        return (
                          <ColorPicker
                            key={subFieldName}
                            value={nestedValue}
                            onChange={handleNestedChange}
                            label={subField.label}
                            helpText={subField.help_text}
                            brandColors={brandColors || {
                              primary: '#2563EB', // Fallback colors if no brand colors are provided
                              secondary: '#7C3AED',
                              tertiary: '#EC4899',
                            }}
                          />
                        );
                      
                      case 'media':
                        return (
                          <FormControl key={subFieldName}>
                            <FormLabel>{subField.label}</FormLabel>
                            {subField.help_text && (
                              <Typography level="body-xs" sx={{ mb: 1 }}>
                                {subField.help_text}
                              </Typography>
                            )}
                            {nestedValue && (
                              <Card variant="outlined" sx={{ mb: 2, position: 'relative' }}>
                                <AspectRatio ratio="16/9">
                                  <img
                                    src={nestedValue}
                                    alt={subField.label}
                                    style={{ objectFit: 'cover' }}
                                  />
                                </AspectRatio>
                                <IconButton
                                  size="sm"
                                  variant="solid"
                                  color="danger"
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 1,
                                  }}
                                  onClick={() => handleNestedChange('')}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Card>
                            )}
                            <Input
                              value={nestedValue}
                              onChange={(e) => handleNestedChange(e.target.value)}
                              placeholder="Select from media library"
                              endDecorator={
                                <IconButton
                                  onClick={() => setMediaPickerField(`${field.name}.${subFieldName}`)}
                                  disabled={!siteId}
                                >
                                  <ImageIcon />
                                </IconButton>
                              }
                            />
                          </FormControl>
                        );
                      
                      case 'range':
                        return (
                          <FormControl key={subFieldName}>
                            <FormLabel>
                              {subField.label}
                              {subField.suffix && (
                                <Chip size="sm" sx={{ ml: 1 }}>
                                  {nestedValue}{subField.suffix}
                                </Chip>
                              )}
                            </FormLabel>
                            {subField.help_text && (
                              <Typography level="body-xs" sx={{ mb: 1 }}>
                                {subField.help_text}
                              </Typography>
                            )}
                            <Slider
                              value={nestedValue || subField.default || subField.min || 0}
                              onChange={(_, newValue) => handleNestedChange(newValue)}
                              min={subField.min || 0}
                              max={subField.max || 100}
                              step={subField.step || 1}
                              valueLabelDisplay="auto"
                              marks={[
                                { value: subField.min || 0, label: `${subField.min || 0}${subField.suffix || ''}` },
                                { value: subField.max || 100, label: `${subField.max || 100}${subField.suffix || ''}` },
                              ]}
                            />
                          </FormControl>
                        );
                      
                      case 'select':
                        return (
                          <FormControl key={subFieldName}>
                            <FormLabel>{subField.label}</FormLabel>
                            {subField.help_text && (
                              <Typography level="body-xs" sx={{ mb: 1 }}>
                                {subField.help_text}
                              </Typography>
                            )}
                            <Select
                              value={nestedValue || subField.default || ''}
                              onChange={(_, newValue) => handleNestedChange(newValue)}
                              placeholder="Choose an option"
                            >
                              {subField.options?.map((option: any) => (
                                <Option key={option.value} value={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      
                      default:
                        return (
                          <FormControl key={subFieldName}>
                            <FormLabel>{subField.label}</FormLabel>
                            <Input
                              value={nestedValue}
                              onChange={(e) => handleNestedChange(e.target.value)}
                            />
                          </FormControl>
                        );
                    }
                  };
                  
                  return <NestedField key={subFieldName} />;
                })}
              </Stack>
            </AccordionDetails>
          </TabbableAccordion>
        );

      default:
        return (
          <FormControl key={field.name}>
            <FormLabel>{field.label}</FormLabel>
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
            />
          </FormControl>
        );
    }
  };

  const aiEnabledFields = sectionConfig?.fields 
    ? Object.entries(sectionConfig.fields)
        .filter(([_, field]: [string, any]) => field.ai_enabled)
        .map(([name, field]: [string, any]) => ({ ...field, name }))
    : [];

  return (
    <>
      <Modal open onClose={onClose}>
        <ModalDialog sx={{ width: '600px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="h4">{sectionConfig?.name || section.type} Settings</Typography>
              {aiEnabledFields.length > 0 && (
                <Button
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={<AIIcon />}
                  onClick={() => setShowAIPanel(true)}
                >
                  AI Bulk Generate
                </Button>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {sectionConfig?.fields &&
                Object.entries(sectionConfig.fields).map(([name, field]: [string, any]) => {
                  // Skip hidden fields
                  if (field.hidden) return null;
                  // Check show_if condition at root level
                  if (field.show_if) {
                    const shouldShow = Object.entries(field.show_if).every(([condField, condValues]: [string, any]) => {
                      // Get current value, or fall back to the field's default
                      const condFieldConfig = sectionConfig?.fields?.[condField];
                      const currentValue = formData[condField] ?? condFieldConfig?.default;
                      if (Array.isArray(condValues)) {
                        return condValues.includes(currentValue);
                      }
                      return currentValue === condValues;
                    });
                    if (!shouldShow) return null;
                  }
                  return renderField({ ...field, name });
                })
              }
              
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="plain" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  Save Section
                </Button>
              </Box>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
      
      {mediaPickerField && siteId && (
        <MediaPicker
          open={!!mediaPickerField}
          onClose={() => setMediaPickerField(null)}
          onSelect={(media) => {
            if (mediaPickerField) {
              const parts = mediaPickerField.split('.');
              if (parts.length === 3) {
                // List item media: "logos.0.image"
                const [listField, indexStr, subField] = parts;
                const index = parseInt(indexStr, 10);
                const listValues = [...(formData[listField] || [])];
                listValues[index] = { ...listValues[index], [subField]: media.url };
                handleFieldChange(listField, listValues);
              } else if (parts.length === 2) {
                // Group field: "background_settings.background_image"
                const [parentField, childField] = parts;
                const parentValue = formData[parentField] || {};
                handleFieldChange(parentField, { ...parentValue, [childField]: media.url });
              } else {
                // Simple field
                handleFieldChange(mediaPickerField, media.url);
              }
              setMediaPickerField(null);
            }
          }}
          siteId={siteId}
          sectionType={section.type}
        />
      )}
      
      {/* Tone Selection Menu */}
      <Menu
        anchorEl={toneMenuAnchor}
        open={Boolean(toneMenuAnchor)}
        onClose={() => setToneMenuAnchor(null)}
        placement="bottom-start"
      >
        <MenuItem disabled>
          <Typography level="body-xs" fontWeight="md">Select Tone</Typography>
        </MenuItem>
        {TONE_PRESETS.map((tone) => (
          <MenuItem
            key={tone.value}
            onClick={() => handleSelectTone(tone.value)}
            selected={selectedTone === tone.value}
          >
            <ListItemDecorator>{tone.icon}</ListItemDecorator>
            {tone.label}
          </MenuItem>
        ))}
      </Menu>
      
      {/* AI Content Panel */}
      {showAIPanel && (
        <AIContentPanel
          open={showAIPanel}
          onClose={() => setShowAIPanel(false)}
          sectionType={section.type}
          fields={aiEnabledFields}
          currentData={formData}
          aiSettings={{ ...aiSettings, tone: selectedTone }}
          contentBrief={contentBrief}
          onGenerate={handleBulkGenerate}
        />
      )}
    </>
  );
};